/**
 * Gear. Every piece you carry is its own instance: a base, a rarity, the tier it
 * was found at, and whatever went wrong with it on the way to you.
 *
 * computeStats() is the one place a person's numbers come from. Combat, speed,
 * soak, the HUD and every tooltip read it. A new affix means one edit here.
 */
import type { OriginId } from './types.ts';

export type Slot = 'weapon' | 'head' | 'chest' | 'legs' | 'hands' | 'charm';
export type EquipSlot = 'hand' | 'sling' | 'head' | 'chest' | 'legs' | 'hands' | 'charm';
export const EQUIP_SLOTS: EquipSlot[] = ['hand', 'sling', 'head', 'chest', 'legs', 'hands', 'charm'];
export const EQUIP_LABEL: Record<EquipSlot, string> = {
  hand: 'Hand',
  sling: 'Sling',
  head: 'Head',
  chest: 'Chest',
  legs: 'Legs',
  hands: 'Hands',
  charm: 'Charm',
};

export type ShotKind = 'bolt' | 'stun' | 'laser' | 'pop' | 'bell' | 'suck' | 'fan';

/** How the piece is drawn in a hand or in a pocket. */
export type Art =
  | 'wrench'
  | 'pipe'
  | 'blade'
  | 'keel'
  | 'maul'
  | 'tooth'
  | 'axe'
  | 'machete'
  | 'hammer'
  | 'saw'
  | 'spike'
  | 'dipper'
  | 'zip'
  | 'prod'
  | 'coil'
  | 'prong'
  | 'needle'
  | 'rod'
  | 'can'
  | 'scatter'
  | 'rifle'
  | 'box'
  | 'cap'
  | 'helm'
  | 'hood'
  | 'mask'
  | 'crown'
  | 'shirt'
  | 'apron'
  | 'coat'
  | 'vest'
  | 'plate'
  | 'pants'
  | 'boots'
  | 'greaves'
  | 'gloves'
  | 'mitts'
  | 'knuckles'
  | 'gauntlets'
  | 'bead'
  | 'foot'
  | 'bottlecap'
  | 'cassette'
  | 'toothcharm'
  | 'key';

export type Base = {
  id: string;
  name: string;
  blurb: string;
  slot: Slot;
  kind: 'melee' | 'ranged' | 'armor' | 'charm';
  /** Where it starts showing up. 1 is the diner, 8 is as far as the roads go, 10 is past the end of the line. */
  tier: number;
  price: number;
  art: Art;
  /** Main color when drawn. */
  tint: string;
  dmg?: number;
  range?: number;
  cd?: number;
  shotSpeed?: number;
  shot?: ShotKind;
  blast?: number;
  stun?: number;
  pull?: number;
  /** Fan guns throw this many bolts. */
  pellets?: number;
  armor?: number;
  /** Who stocks it. Undefined means the ground gives it out and nobody sells it. */
  vendor?: 'peck' | 'moss' | 'sump';
  /** Relics are fixed. They drop as themselves, with these. */
  relic?: Affix[];
  /** Never rolled as random loot. Quest rewards and boss relics. */
  noDrop?: boolean;
  /** One piece of a set (SETS). Wearing more of it adds the set's bonus. */
  set?: string;
  /** Sully in Sump sells it, but only to people who took this side at the end of the story. */
  kept?: 'ride' | 'stay';
};

/** The deepest tier anything is found at. The Terminus is 9 and 10. */
export const MAX_TIER = 10;

const B = (base: Base): Base => base;

export const BASE_LIST: Base[] = [
  // Melee.
  B({ id: 'wrench', name: 'Pipe Wrench', blurb: 'Already bent the way you need.', slot: 'weapon', kind: 'melee', tier: 1, price: 8, art: 'wrench', tint: '#b7b1a4', dmg: 10, range: 30, cd: 0.4, vendor: 'peck' }),
  B({ id: 'pipe', name: 'Lead Pipe', blurb: 'Heavy at one end, which is the end that matters.', slot: 'weapon', kind: 'melee', tier: 1, price: 9, art: 'pipe', tint: '#8a8478', dmg: 12, range: 28, cd: 0.48 }),
  B({ id: 'blade', name: 'Hubcap Blade', blurb: 'It remembers being a wheel.', slot: 'weapon', kind: 'melee', tier: 2, price: 22, art: 'blade', tint: '#d9d3c8', dmg: 15, range: 34, cd: 0.46, vendor: 'peck' }),
  B({ id: 'maul', name: 'Rebar Maul', blurb: 'Slow. Then very fast, for the other party.', slot: 'weapon', kind: 'melee', tier: 3, price: 30, art: 'maul', tint: '#8a4a32', dmg: 25, range: 34, cd: 0.72 }),
  B({ id: 'axe', name: 'Sign Axe', blurb: 'The old sign said YIELD. It stopped taking its own advice.', slot: 'weapon', kind: 'melee', tier: 4, price: 36, art: 'axe', tint: '#c4a15a', dmg: 24, range: 34, cd: 0.54 }),
  B({ id: 'machete', name: 'Glass Machete', blurb: 'Grown in the lamp rows. It sings a little when it is used right.', slot: 'weapon', kind: 'melee', tier: 5, price: 44, art: 'machete', tint: '#7fbfa3', dmg: 26, range: 36, cd: 0.42 }),
  B({ id: 'hammer', name: 'Pylon Hammer', blurb: 'The head used to hold up a wire. Now it holds a grudge.', slot: 'weapon', kind: 'melee', tier: 6, price: 56, art: 'hammer', tint: '#6d7c86', dmg: 40, range: 36, cd: 0.78 }),
  B({ id: 'saw', name: 'Bone Saw', blurb: 'Someone in Sump sharpens these for a living. Ask how.', slot: 'weapon', kind: 'melee', tier: 7, price: 64, art: 'saw', tint: '#e7d3c4', dmg: 36, range: 34, cd: 0.46, vendor: 'sump' }),
  B({ id: 'spike', name: 'Rail Spike', blurb: 'Driven once. It would like to be driven again.', slot: 'weapon', kind: 'melee', tier: 8, price: 80, art: 'spike', tint: '#5c564e', dmg: 50, range: 38, cd: 0.62 }),
  // Guns, loosely.
  B({ id: 'zip', name: 'Zip Gun', blurb: 'One barrel, strong opinions.', slot: 'weapon', kind: 'ranged', tier: 1, price: 10, art: 'zip', tint: '#5c564e', dmg: 8, cd: 0.62, shotSpeed: 240, vendor: 'peck' }),
  B({ id: 'prod', name: 'Stun Prod', blurb: 'A rude little spark. It stops a thing from walking for a moment.', slot: 'weapon', kind: 'ranged', tier: 1, price: 14, art: 'prod', tint: '#7fbfb2', dmg: 5, cd: 0.55, shotSpeed: 210, shot: 'stun', stun: 1.25, vendor: 'peck' }),
  B({ id: 'pop', name: 'Pop Can', blurb: 'Throw it. It blooms. Everyone in the bloom gets the same bad news.', slot: 'weapon', kind: 'ranged', tier: 2, price: 18, art: 'can', tint: '#e07a3d', dmg: 10, cd: 1.05, shotSpeed: 150, shot: 'pop', blast: 40, vendor: 'peck' }),
  B({ id: 'coil', name: 'Coil Spit', blurb: 'Smells like a storm gutter.', slot: 'weapon', kind: 'ranged', tier: 2, price: 26, art: 'coil', tint: '#7fbfb2', dmg: 13, cd: 0.5, shotSpeed: 300, vendor: 'peck' }),
  B({ id: 'needle', name: 'Needle Lamp', blurb: 'A thin white line. It stops at the first wall, or the first body.', slot: 'weapon', kind: 'ranged', tier: 3, price: 30, art: 'needle', tint: '#f4f1ea', dmg: 11, cd: 0.8, range: 200, shot: 'laser', vendor: 'moss' }),
  B({ id: 'bell', name: 'Bell Can', blurb: 'A soft pop that tells a whole crowd to sit down.', slot: 'weapon', kind: 'ranged', tier: 3, price: 20, art: 'can', tint: '#e7d7a4', dmg: 3, cd: 1.25, shotSpeed: 140, shot: 'bell', blast: 46, stun: 1.5, vendor: 'moss' }),
  B({ id: 'suck', name: 'Suck Tin', blurb: 'It inhales. Things nearby come over to complain.', slot: 'weapon', kind: 'ranged', tier: 3, price: 24, art: 'can', tint: '#b48cdc', dmg: 4, cd: 1.4, shotSpeed: 130, shot: 'suck', blast: 56, pull: 0.7, vendor: 'moss' }),
  B({ id: 'scatter', name: 'Scatter Can', blurb: 'Three opinions at once. None of them polite.', slot: 'weapon', kind: 'ranged', tier: 4, price: 38, art: 'scatter', tint: '#a34532', dmg: 9, cd: 0.8, shotSpeed: 230, shot: 'fan', pellets: 3, vendor: 'moss' }),
  B({ id: 'rod', name: 'Leak Rod', blurb: 'It pays better because it is farther out, and it knows.', slot: 'weapon', kind: 'ranged', tier: 5, price: 36, art: 'rod', tint: '#8fbf6a', dmg: 18, cd: 0.46, shotSpeed: 280 }),
  B({ id: 'rifle', name: 'Drip Rifle', blurb: 'Long, black, and patient. The shot arrives before the apology.', slot: 'weapon', kind: 'ranged', tier: 6, price: 58, art: 'rifle', tint: '#3f6b5a', dmg: 30, cd: 0.9, shotSpeed: 380, vendor: 'sump' }),
  B({ id: 'lamprifle', name: 'Lamp Rifle', blurb: 'A needle lamp that grew up and got a job.', slot: 'weapon', kind: 'ranged', tier: 7, price: 70, art: 'needle', tint: '#e0b15a', dmg: 26, cd: 0.7, range: 240, shot: 'laser' }),
  B({ id: 'humbox', name: 'Hum Box', blurb: 'Five notes. The chord is bad news.', slot: 'weapon', kind: 'ranged', tier: 8, price: 84, art: 'box', tint: '#7fbfb2', dmg: 14, cd: 0.9, shotSpeed: 250, shot: 'fan', pellets: 5 }),
  // Head.
  B({ id: 'cap', name: 'Tin Cap', blurb: 'Three bits of scrap and a prayer. Adequate.', slot: 'head', kind: 'armor', tier: 1, price: 4, art: 'cap', tint: '#8a8478', armor: 1 }),
  B({ id: 'helm', name: 'Bowl Helm', blurb: 'It rang when you found it. Better than the cap.', slot: 'head', kind: 'armor', tier: 2, price: 8, art: 'helm', tint: '#6a6560', armor: 2 }),
  B({ id: 'hubhat', name: 'Hubcap Hat', blurb: 'It covers most of the important part.', slot: 'head', kind: 'armor', tier: 3, price: 14, art: 'helm', tint: '#d9d3c8', armor: 3 }),
  B({ id: 'mask', name: 'Welding Mask', blurb: 'You see less. Less sees you.', slot: 'head', kind: 'armor', tier: 4, price: 18, art: 'mask', tint: '#3a3228', armor: 3, vendor: 'moss' }),
  B({ id: 'cowl', name: 'Count Cowl', blurb: 'A hood that argues with the shine. The glow has less to say.', slot: 'head', kind: 'armor', tier: 5, price: 16, art: 'hood', tint: '#2f4a28', armor: 4 }),
  B({ id: 'pothelm', name: 'Stock Pot', blurb: 'Soup used to be inside. You are the soup now.', slot: 'head', kind: 'armor', tier: 6, price: 30, art: 'helm', tint: '#8a8478', armor: 5, vendor: 'sump' }),
  B({ id: 'crown', name: 'Choir Crown', blurb: 'Copper, and still humming the last hymn it heard.', slot: 'head', kind: 'armor', tier: 8, price: 50, art: 'crown', tint: '#c47a4a', armor: 7 }),
  // Chest.
  B({ id: 'shirt', name: 'Stitch Shirt', blurb: 'Rags with ambitions. Adequate.', slot: 'chest', kind: 'armor', tier: 1, price: 4, art: 'shirt', tint: '#a34532', armor: 1 }),
  B({ id: 'apron', name: 'Diner Apron', blurb: 'Grease is a kind of armor.', slot: 'chest', kind: 'armor', tier: 1, price: 6, art: 'apron', tint: '#d7c7a4', armor: 2, vendor: 'peck' }),
  B({ id: 'coat', name: 'Quilted Coat', blurb: 'Someone else stuffed it. It holds.', slot: 'chest', kind: 'armor', tier: 2, price: 14, art: 'coat', tint: '#6d5432', armor: 3 }),
  B({ id: 'sign', name: 'Road-Sign Vest', blurb: "Someone else's speed limit.", slot: 'chest', kind: 'armor', tier: 3, price: 18, art: 'vest', tint: '#e0b15a', armor: 5, vendor: 'peck' }),
  B({ id: 'tire', name: 'Tire Vest', blurb: 'It still has tread. You will not skid.', slot: 'chest', kind: 'armor', tier: 4, price: 26, art: 'vest', tint: '#2a241c', armor: 6, vendor: 'moss' }),
  B({ id: 'lead', name: 'Lead Wrap', blurb: 'Heavy enough that the glow gives up. A pool still wants a taste.', slot: 'chest', kind: 'armor', tier: 5, price: 32, art: 'plate', tint: '#6a6e68', armor: 7 }),
  B({ id: 'poncho', name: 'Plate Poncho', blurb: 'Dinner plates, wired together. Do not eat off it.', slot: 'chest', kind: 'armor', tier: 6, price: 40, art: 'plate', tint: '#efeae0', armor: 8, vendor: 'sump' }),
  B({ id: 'stove', name: 'Stove Coat', blurb: 'It is warm on the inside and on the outside, which is the problem.', slot: 'chest', kind: 'armor', tier: 7, price: 48, art: 'coat', tint: '#3a3a38', armor: 10 }),
  B({ id: 'hull', name: 'Hull Jacket', blurb: 'A piece of a boat that got a long way from water.', slot: 'chest', kind: 'armor', tier: 8, price: 60, art: 'plate', tint: '#3c5e74', armor: 12 }),
  // Legs.
  B({ id: 'pants', name: 'Patch Pants', blurb: 'The knees are a negotiation.', slot: 'legs', kind: 'armor', tier: 1, price: 6, art: 'pants', tint: '#3c5e74', armor: 1 }),
  B({ id: 'boots', name: 'Road Boots', blurb: 'Heavy, and that is the point.', slot: 'legs', kind: 'armor', tier: 2, price: 8, art: 'boots', tint: '#6d5432', armor: 2 }),
  B({ id: 'chaps', name: 'Chain Chaps', blurb: 'They clink. Things hear you coming and reconsider.', slot: 'legs', kind: 'armor', tier: 3, price: 16, art: 'pants', tint: '#8a8478', armor: 3, vendor: 'moss' }),
  B({ id: 'waders', name: 'Seep Waders', blurb: 'The pool bites the boots first.', slot: 'legs', kind: 'armor', tier: 5, price: 18, art: 'boots', tint: '#3d6b22', armor: 4 }),
  B({ id: 'greaves', name: 'Drum Greaves', blurb: 'Oil drum, cut to shin. Loud to kneel in.', slot: 'legs', kind: 'armor', tier: 6, price: 30, art: 'greaves', tint: '#6a3832', armor: 5, vendor: 'sump' }),
  B({ id: 'railboots', name: 'Rail Boots', blurb: 'Steel soles. The ground learns who is walking.', slot: 'legs', kind: 'armor', tier: 8, price: 46, art: 'boots', tint: '#5c564e', armor: 7 }),
  // Hands.
  B({ id: 'gloves', name: 'Work Gloves', blurb: 'The fingers are someone else’s shape.', slot: 'hands', kind: 'armor', tier: 1, price: 5, art: 'gloves', tint: '#c4a15a', armor: 1, vendor: 'peck' }),
  B({ id: 'mitts', name: 'Oven Mitts', blurb: 'Rated for pans. Adequate for roaches.', slot: 'hands', kind: 'armor', tier: 2, price: 9, art: 'mitts', tint: '#a34532', armor: 1 }),
  B({ id: 'knuckles', name: 'Wire Knuckles', blurb: 'Copper around each finger. Every punch is a short circuit.', slot: 'hands', kind: 'armor', tier: 4, price: 20, art: 'knuckles', tint: '#c47a4a', armor: 2, vendor: 'moss' }),
  B({ id: 'gauntlets', name: 'Glow Gauntlets', blurb: 'They light up when you are about to make a mistake.', slot: 'hands', kind: 'armor', tier: 6, price: 34, art: 'gauntlets', tint: '#8fbf6a', armor: 3 }),
  // Charms. A plain charm is nothing, so charms are never plain.
  B({ id: 'bottlecap', name: 'Bottle Cap', blurb: 'Bent into a shape that means something to somebody.', slot: 'charm', kind: 'charm', tier: 1, price: 10, art: 'bottlecap', tint: '#e0b15a' }),
  B({ id: 'foot', name: 'Roach Foot', blurb: 'Lucky for you. Not for the roach.', slot: 'charm', kind: 'charm', tier: 2, price: 14, art: 'foot', tint: '#5e7a45' }),
  B({ id: 'cassette', name: 'Cassette', blurb: 'Side B is just breathing.', slot: 'charm', kind: 'charm', tier: 3, price: 18, art: 'cassette', tint: '#3a3228' }),
  B({ id: 'toothcharm', name: 'Tooth on a String', blurb: 'Not yours. Probably.', slot: 'charm', kind: 'charm', tier: 4, price: 22, art: 'toothcharm', tint: '#e7d3c4' }),
  B({ id: 'bead', name: 'Glow Bead', blurb: 'Warm in the hand. Counting something.', slot: 'charm', kind: 'charm', tier: 5, price: 28, art: 'bead', tint: '#d7e46a' }),
  B({ id: 'key', name: 'Nobody’s Key', blurb: 'It opens a door that is not here anymore.', slot: 'charm', kind: 'charm', tier: 7, price: 40, art: 'key', tint: '#c4a574' }),
  // Relics. Named, fixed, and earned.
  B({ id: 'dipper', name: 'Well Dipper', blurb: 'It came up ringing.', slot: 'weapon', kind: 'melee', tier: 2, price: 20, art: 'dipper', tint: '#7fbfb2', dmg: 16, range: 34, cd: 0.42, noDrop: true, relic: [{ stat: 'mend', v: 1 }, { stat: 'hp', v: 6 }] }),
  B({ id: 'keel', name: 'Pan Keel', blurb: 'It came up white. The edge is a rumor that cuts.', slot: 'weapon', kind: 'melee', tier: 3, price: 30, art: 'keel', tint: '#f4f1ea', dmg: 18, range: 36, cd: 0.5, noDrop: true, relic: [{ stat: 'crit', v: 10 }, { stat: 'dmg', v: 2 }] }),
  B({ id: 'prong', name: 'Static Prong', blurb: 'It hums in your teeth. The court was using it as a hymn.', slot: 'weapon', kind: 'ranged', tier: 3, price: 40, art: 'prong', tint: '#7fbfb2', dmg: 15, cd: 0.52, shotSpeed: 270, noDrop: true, relic: [{ stat: 'stun', v: 15 }, { stat: 'speed', v: 10 }] }),
  B({ id: 'tooth', name: 'Drawer Tooth', blurb: 'It was keeping a shirt. The shirt lost.', slot: 'weapon', kind: 'melee', tier: 4, price: 44, art: 'tooth', tint: '#e7d3c4', dmg: 21, range: 32, cd: 0.48, noDrop: true, relic: [{ stat: 'leech', v: 2 }, { stat: 'pct', v: 15 }] }),
  B({ id: 'cushion', name: 'Coach Cushion', blurb: 'Vetch let you keep the spring. It remembers a person, and now it remembers you.', slot: 'charm', kind: 'charm', tier: 4, price: 30, art: 'bottlecap', tint: '#a34532', noDrop: true, relic: [{ stat: 'hp', v: 8 }, { stat: 'mend', v: 1 }] }),
  B({ id: 'ticket', name: 'Punched Ticket', blurb: 'Brine\u2019s. Punched once, a long time ago. It still wants to be somewhere.', slot: 'charm', kind: 'charm', tier: 5, price: 60, art: 'bottlecap', tint: '#d7c7a4', noDrop: true, relic: [{ stat: 'find', v: 20 }, { stat: 'speed', v: 8 }, { stat: 'tabs', v: 15 }] }),
  B({ id: 'spanner', name: 'Foreman\u2019s Spanner', blurb: 'It fits every nut in the Undertrack, and some that are not nuts.', slot: 'weapon', kind: 'melee', tier: 6, price: 70, art: 'wrench', tint: '#c47a4a', dmg: 34, range: 34, cd: 0.46, noDrop: true, relic: [{ stat: 'stun', v: 15 }, { stat: 'pct', v: 15 }, { stat: 'leech', v: 2 }] }),
  B({ id: 'stub', name: 'Ticket Stub', blurb: 'Torn along the dotted line. The other half went somewhere else.', slot: 'charm', kind: 'charm', tier: 7, price: 80, art: 'bottlecap', tint: '#d7c7a4', noDrop: true, relic: [{ stat: 'crit', v: 10 }, { stat: 'hp', v: 14 }, { stat: 'find', v: 15 }] }),
  // Act three. The coat, the bosses past the edge, and the two sets, one for each way the story ends.
  B({ id: 'condcoat', name: 'Conductor\u2019s Coat', blurb: 'Brass buttons and a whistle on a chain. Still warm. It fits, which it should not.', slot: 'chest', kind: 'armor', tier: 8, price: 90, art: 'coat', tint: '#34405e', armor: 11, noDrop: true, relic: [{ stat: 'speed', v: 10 }, { stat: 'hp', v: 14 }, { stat: 'find', v: 20 }] }),
  B({ id: 'chain', name: 'Surveyor\u2019s Chain', blurb: 'Exactly one chain long. It measures whatever it hits, and finds it short.', slot: 'weapon', kind: 'melee', tier: 8, price: 100, art: 'coil', tint: '#c4a574', dmg: 46, range: 50, cd: 0.62, noDrop: true, relic: [{ stat: 'reach', v: 10 }, { stat: 'pct', v: 20 }, { stat: 'stun', v: 10 }] }),
  B({ id: 'spring', name: 'Mainspring', blurb: 'Wound once, a long time ago. It is still letting go.', slot: 'charm', kind: 'charm', tier: 8, price: 100, art: 'bead', tint: '#c47a4a', noDrop: true, relic: [{ stat: 'fast', v: 15 }, { stat: 'mend', v: 1 }, { stat: 'hp', v: 12 }] }),
  B({ id: 'nought', name: 'Nought', blurb: 'The last number. It is round, and empty, and it hits like everything before it.', slot: 'weapon', kind: 'ranged', tier: 10, price: 140, art: 'box', tint: '#efeae0', dmg: 40, cd: 0.6, shotSpeed: 300, shot: 'bell', blast: 44, noDrop: true, relic: [{ stat: 'crit', v: 12 }, { stat: 'pct', v: 25 }, { stat: 'leech', v: 3 }] }),
  B({ id: 'punch', name: 'Conductor\u2019s Punch', blurb: 'From Brine\u2019s apron, which she was not wearing. Every hole is a stop.', slot: 'hands', kind: 'armor', tier: 9, price: 120, art: 'knuckles', tint: '#c4a15a', armor: 4, noDrop: true, set: 'through', relic: [{ stat: 'dmg', v: 4 }, { stat: 'crit', v: 8 }, { stat: 'tabs', v: 20 }] }),
  B({ id: 'guardcap', name: 'Guard\u2019s Cap', blurb: 'Peaked, patient, and never once late.', slot: 'head', kind: 'armor', tier: 9, price: 150, art: 'cap', tint: '#34405e', armor: 7, noDrop: true, set: 'through', kept: 'ride', relic: [{ stat: 'find', v: 25 }, { stat: 'hp', v: 12 }] }),
  B({ id: 'guardlamp', name: 'Guard\u2019s Lamp', blurb: 'Green for go. It has never shown anything else.', slot: 'weapon', kind: 'ranged', tier: 9, price: 170, art: 'needle', tint: '#7fbf6a', dmg: 30, cd: 0.52, range: 240, shot: 'laser', noDrop: true, set: 'through', kept: 'ride', relic: [{ stat: 'fast', v: 12 }, { stat: 'crit', v: 8 }] }),
  B({ id: 'minutehand', name: 'Minute Hand', blurb: 'Long and black. It stopped on nothing, and it stays stopped.', slot: 'weapon', kind: 'melee', tier: 9, price: 120, art: 'spike', tint: '#2a2622', dmg: 54, range: 40, cd: 0.56, noDrop: true, set: 'stopped', relic: [{ stat: 'pct', v: 15 }, { stat: 'leech', v: 2 }] }),
  B({ id: 'stoppedface', name: 'Stopped Face', blurb: 'A clock face with no hands, worn as a visor. Nothing is late while you wear it.', slot: 'head', kind: 'armor', tier: 9, price: 150, art: 'mask', tint: '#efeae0', armor: 8, noDrop: true, set: 'stopped', kept: 'stay', relic: [{ stat: 'hp', v: 16 }, { stat: 'mend', v: 1 }] }),
  B({ id: 'pendulum', name: 'Pendulum Greaves', blurb: 'Heavy at the bottom. They swing back, and so do you.', slot: 'legs', kind: 'armor', tier: 9, price: 150, art: 'greaves', tint: '#8a6a42', armor: 8, noDrop: true, set: 'stopped', kept: 'stay', relic: [{ stat: 'thorns', v: 6 }, { stat: 'hp', v: 12 }] }),
  // Found only at the Terminus.
  B({ id: 'lever', name: 'Switch Lever', blurb: 'It sent a lot of trains the wrong way. Now it sends people.', slot: 'weapon', kind: 'melee', tier: 9, price: 90, art: 'hammer', tint: '#a34532', dmg: 58, range: 38, cd: 0.66 }),
  B({ id: 'signal', name: 'Signal Lamp', blurb: 'Red means stop. It means it very hard.', slot: 'weapon', kind: 'ranged', tier: 9, price: 92, art: 'rifle', tint: '#d4543a', dmg: 34, cd: 0.72, shotSpeed: 380 }),
  B({ id: 'porter', name: 'Porter\u2019s Coat', blurb: 'Carried a thousand bags. It can carry you.', slot: 'chest', kind: 'armor', tier: 9, price: 70, art: 'coat', tint: '#6d5432', armor: 14 }),
  B({ id: 'countface', name: 'The Count’s Face', blurb: 'It keeps counting after you take it off. Leave it on.', slot: 'head', kind: 'armor', tier: 6, price: 60, art: 'mask', tint: '#8fbf6a', armor: 5, noDrop: true, relic: [{ stat: 'glow', v: 3 }, { stat: 'hp', v: 10 }, { stat: 'crit', v: 6 }] }),
  B({ id: 'mastbolt', name: 'Mast Bolt', blurb: 'The antenna court was aiming this at the sky. You aim it lower.', slot: 'weapon', kind: 'ranged', tier: 4, price: 50, art: 'prong', tint: '#e0b15a', dmg: 16, cd: 0.44, shotSpeed: 320, noDrop: true, relic: [{ stat: 'fast', v: 12 }, { stat: 'crit', v: 8 }] }),
  B({ id: 'drawerlid', name: 'Warm Lid', blurb: 'From the Warm Drawer. It still keeps things.', slot: 'chest', kind: 'armor', tier: 4, price: 50, art: 'plate', tint: '#e07a3d', armor: 7, noDrop: true, relic: [{ stat: 'thorns', v: 4 }, { stat: 'mend', v: 1 }] }),
  B({ id: 'wanderer', name: 'Wanderer’s Walk', blurb: 'Taken off something that never sat down. Your feet agree with it now.', slot: 'legs', kind: 'armor', tier: 7, price: 80, art: 'boots', tint: '#b48cdc', armor: 6, noDrop: true, relic: [{ stat: 'speed', v: 18 }, { stat: 'hp', v: 12 }, { stat: 'find', v: 30 }] }),
  B({ id: 'kettlecrown', name: 'Kettle Crown', blurb: 'Boss of the flats. It whistles when you are winning.', slot: 'head', kind: 'armor', tier: 7, price: 80, art: 'crown', tint: '#e07a3d', armor: 7, noDrop: true, relic: [{ stat: 'pct', v: 20 }, { stat: 'hp', v: 10 }] }),
  B({ id: 'lastlamp', name: 'The Last Lamp', blurb: 'From the bottom of the dark station. It does not turn off.', slot: 'weapon', kind: 'ranged', tier: 8, price: 100, art: 'needle', tint: '#f4f1ea', dmg: 34, cd: 0.6, range: 260, shot: 'laser', noDrop: true, relic: [{ stat: 'crit', v: 12 }, { stat: 'leech', v: 3 }, { stat: 'pct', v: 20 }] }),
];

export const BASES: Record<string, Base> = Object.fromEntries(BASE_LIST.map((base) => [base.id, base]));

export function isBaseId(value: unknown): value is string {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(BASES, value);
}

// ---------------------------------------------------------------------------
// Affixes.

export type Stat =
  | 'dmg'
  | 'pct'
  | 'armor'
  | 'hp'
  | 'glow'
  | 'stun'
  | 'fast'
  | 'speed'
  | 'leech'
  | 'mend'
  | 'thorns'
  | 'tabs'
  | 'crit'
  | 'reach'
  | 'find';

export type Affix = { stat: Stat; v: number };

type AffixDef = {
  stat: Stat;
  kind: 'prefix' | 'suffix';
  name: string;
  slots: Slot[];
  /** Melee-only or ranged-only weapon affixes. */
  only?: 'melee' | 'ranged';
  min: number;
  max: number;
};

/** Values are the tier-1 range. Deeper tiers scale them up. */
const AFFIXES: AffixDef[] = [
  { stat: 'dmg', kind: 'prefix', name: 'Mean', slots: ['weapon', 'hands', 'charm'], min: 1, max: 3 },
  { stat: 'pct', kind: 'prefix', name: 'Spiteful', slots: ['weapon', 'charm'], min: 8, max: 18 },
  { stat: 'armor', kind: 'prefix', name: 'Thick', slots: ['head', 'chest', 'legs', 'hands'], min: 1, max: 2 },
  { stat: 'hp', kind: 'prefix', name: 'Stitched', slots: ['head', 'chest', 'legs', 'hands', 'charm'], min: 3, max: 7 },
  { stat: 'glow', kind: 'prefix', name: 'Leaded', slots: ['head', 'chest', 'legs', 'charm'], min: 1, max: 1 },
  { stat: 'stun', kind: 'prefix', name: 'Humming', slots: ['weapon', 'hands'], min: 5, max: 10 },
  { stat: 'fast', kind: 'suffix', name: 'of the Twitch', slots: ['weapon', 'hands', 'charm'], min: 6, max: 12 },
  { stat: 'speed', kind: 'suffix', name: 'of the Mile', slots: ['legs', 'charm'], min: 4, max: 8 },
  { stat: 'leech', kind: 'suffix', name: 'of the Tick', slots: ['weapon', 'hands', 'charm'], min: 1, max: 1 },
  { stat: 'mend', kind: 'suffix', name: "of Brine's Patience", slots: ['head', 'chest', 'charm'], min: 1, max: 1 },
  { stat: 'thorns', kind: 'suffix', name: 'that Bites Back', slots: ['chest', 'hands', 'legs'], min: 1, max: 3 },
  { stat: 'tabs', kind: 'suffix', name: "of Peck's Thumb", slots: ['head', 'hands', 'charm'], min: 10, max: 25 },
  { stat: 'crit', kind: 'suffix', name: 'of the Wrong Angle', slots: ['weapon', 'head', 'charm'], min: 3, max: 7 },
  { stat: 'reach', kind: 'suffix', name: 'of the Long Arm', slots: ['weapon'], only: 'melee', min: 3, max: 6 },
  { stat: 'find', kind: 'suffix', name: 'of the Second Look', slots: ['head', 'charm', 'hands'], min: 8, max: 18 },
];

export const STAT_LABEL: Record<Stat, (v: number) => string> = {
  dmg: (v) => `+${v} damage`,
  pct: (v) => `+${v}% damage`,
  armor: (v) => `+${v} soak`,
  hp: (v) => `+${v} health`,
  glow: (v) => `Shrugs off ${v} of the glow`,
  stun: (v) => `${v}% chance to stun`,
  fast: (v) => `+${v}% attack speed`,
  speed: (v) => `+${v}% walking speed`,
  leech: (v) => `+${v} health per hit`,
  mend: (v) => `Mends ${v} health every 3 seconds`,
  thorns: (v) => `Bites back for ${v}`,
  tabs: (v) => `+${v}% tabs found`,
  crit: (v) => `${v}% chance to hit twice as hard`,
  reach: (v) => `+${v} reach`,
  find: (v) => `+${v}% better finds`,
};

// ---------------------------------------------------------------------------
// Rarity.

export type Rarity = 'plain' | 'odd' | 'wrong' | 'relic';
export const RARITY_NAME: Record<Rarity, string> = { plain: 'Plain', odd: 'Odd', wrong: 'Wrong', relic: 'Relic' };
export const RARITY_COLOR: Record<Rarity, string> = {
  plain: '#e7d7c8',
  odd: '#7fbfb2',
  wrong: '#e0b15a',
  relic: '#e07a3d',
};

const WRONG_A = ['Grease', 'Dust', 'Mile', 'Glow', 'Bone', 'Tin', 'Salt', 'Choir', 'Rust', 'Static', 'Marrow', 'Kettle', 'Porch', 'Lamp', "Nub's", 'Supper', 'Coach', 'Well'];
const WRONG_B = ['Promise', 'Opinion', 'Hymn', 'Tab', 'Knuckle', 'Grudge', 'Rumor', 'Verse', 'Argument', 'Receipt', 'Habit', 'Apology', 'Sermon', 'Alibi', 'Errand', 'Tantrum'];

export type ItemInst = {
  uid: string;
  base: string;
  rarity: Rarity;
  /** The tier it was found at. */
  tier: number;
  affixes: Affix[];
  /** Only Wrong pieces get their own name. */
  name?: string;
};

let uidCount = 0;
export function newUid(): string {
  uidCount = (uidCount + 1) % 1_679_616;
  return Date.now().toString(36).slice(-5) + uidCount.toString(36).padStart(4, '0') + Math.floor(Math.random() * 1296).toString(36).padStart(2, '0');
}

export type Rng = () => number;

function tierScale(tier: number): number {
  return 1 + 0.35 * Math.max(0, tier - 1);
}

function rollAffix(def: AffixDef, tier: number, rng: Rng): Affix {
  const base = def.min + rng() * (def.max - def.min);
  const scaled = def.stat === 'glow' || def.stat === 'leech' || def.stat === 'mend' ? base + Math.floor((tier - 1) / 3) : base * tierScale(tier);
  const cap = def.stat === 'fast' || def.stat === 'speed' ? 30 : def.stat === 'crit' || def.stat === 'stun' ? 25 : 999;
  return { stat: def.stat, v: Math.min(cap, Math.max(1, Math.round(scaled))) };
}

/** 70 / 24 / 6 at no boost. Champions, crates and "find" push it along. */
export function rollRarity(rng: Rng, boost = 1): Rarity {
  const wrong = 6 * boost;
  const odd = 24 * boost;
  const roll = rng() * 100;
  if (roll < wrong) return 'wrong';
  if (roll < wrong + odd) return 'odd';
  return 'plain';
}

function affixPool(base: Base): AffixDef[] {
  return AFFIXES.filter((def) => def.slots.includes(base.slot) && (!def.only || def.only === base.kind));
}

/** A fresh piece of the given base. */
export function makeItem(baseId: string, tier: number, rarity: Rarity, rng: Rng = Math.random): ItemInst {
  const base = BASES[baseId];
  if (!base) throw new Error(`No base ${baseId}`);
  const at = Math.max(1, Math.min(MAX_TIER, Math.round(tier)));
  if (base.relic) return { uid: newUid(), base: base.id, rarity: 'relic', tier: Math.max(at, base.tier), affixes: base.relic.map((a) => ({ ...a })) };
  let r = rarity === 'relic' ? 'wrong' : rarity;
  if (r === 'plain' && base.kind === 'charm') r = 'odd';
  if (r === 'plain') return { uid: newUid(), base: base.id, rarity: 'plain', tier: at, affixes: [] };
  const pool = affixPool(base);
  const taken = new Set<Stat>();
  const chosen: AffixDef[] = [];
  const take = (options: AffixDef[]) => {
    if (!options.length) return;
    const def = options[Math.floor(rng() * options.length)];
    taken.add(def.stat);
    chosen.push(def);
  };
  if (r === 'odd') {
    const shape = rng();
    if (shape < 0.66) take(pool.filter((def) => def.kind === 'prefix'));
    if (shape > 0.33 || chosen.length === 0) take(pool.filter((def) => def.kind === 'suffix' && !taken.has(def.stat)));
    const affixes = chosen.map((def) => rollAffix(def, at, rng));
    return { uid: newUid(), base: base.id, rarity: 'odd', tier: at, affixes };
  }
  const count = 3 + (rng() < 0.35 ? 1 : 0);
  for (let i = 0; i < count; i++) take(pool.filter((def) => !taken.has(def.stat)));
  const affixes = chosen.map((def) => rollAffix(def, at, rng));
  const name = `${WRONG_A[Math.floor(rng() * WRONG_A.length)]} ${WRONG_B[Math.floor(rng() * WRONG_B.length)]}`;
  return { uid: newUid(), base: base.id, rarity: 'wrong', tier: at, affixes, name };
}

/** Which bases can turn up at a tier. Mostly near the tier, a few from under it. */
export function droppableBases(tier: number, slot?: Slot): Base[] {
  return BASE_LIST.filter((base) => !base.noDrop && base.tier <= tier && base.tier >= tier - 3 && (!slot || base.slot === slot));
}

/** A random piece of loot for a place of the given tier. */
export function rollLoot(tier: number, rng: Rng = Math.random, boost = 1): ItemInst | null {
  const bases = droppableBases(tier);
  if (!bases.length) return null;
  // Closer-to-tier bases weigh more, so far places mostly drop far things.
  let total = 0;
  const weights = bases.map((base) => {
    const w = 1 + Math.max(0, 3 - (tier - base.tier)) * 1.5;
    total += w;
    return w;
  });
  let pick = rng() * total;
  let base = bases[bases.length - 1];
  for (let i = 0; i < bases.length; i++) {
    pick -= weights[i];
    if (pick <= 0) {
      base = bases[i];
      break;
    }
  }
  return makeItem(base.id, tier, rollRarity(rng, boost), rng);
}

export function itemName(item: ItemInst): string {
  const base = BASES[item.base];
  if (!base) return 'Something';
  if (item.rarity === 'wrong' && item.name) return item.name;
  if (item.rarity !== 'odd') return base.name;
  const pre = AFFIXES.find((def) => def.kind === 'prefix' && item.affixes.some((a) => a.stat === def.stat));
  const suf = AFFIXES.find((def) => def.kind === 'suffix' && item.affixes.some((a) => a.stat === def.stat));
  return [pre?.name, base.name, suf?.name].filter(Boolean).join(' ');
}

export function sellValue(item: ItemInst): number {
  const base = BASES[item.base];
  if (!base) return 0;
  const mult = item.rarity === 'relic' ? 3 : item.rarity === 'wrong' ? 3.5 : item.rarity === 'odd' ? 1.8 : 1;
  return Math.max(1, Math.floor(base.price * 0.4 * mult * (1 + 0.1 * (item.tier - base.tier)) + item.affixes.length));
}

export function buyPrice(item: ItemInst): number {
  return Math.max(2, Math.ceil(sellValue(item) * 2.6));
}

/** What breaking it down gives back. */
export function salvageOf(item: ItemInst): Partial<Record<'scrap' | 'wire' | 'rag' | 'grit' | 'slag', number>> {
  const base = BASES[item.base];
  const out: Partial<Record<'scrap' | 'wire' | 'rag' | 'grit' | 'slag', number>> = {};
  if (!base) return out;
  if (base.slot === 'chest' || base.slot === 'legs' || base.slot === 'hands') out.rag = 1 + (item.tier >= 4 ? 1 : 0);
  out.scrap = 1 + Math.floor(item.tier / 3);
  if (item.rarity === 'odd' || item.rarity === 'wrong') out.wire = item.rarity === 'wrong' ? 2 : 1;
  if (item.rarity === 'wrong' && item.tier >= 5) out.slag = 1;
  else if (item.rarity === 'wrong') out.grit = 1;
  return out;
}

// ---------------------------------------------------------------------------
// Validation, for saves and for anything a client sends.

export function cleanItem(raw: unknown): ItemInst | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (!isBaseId(value.base) || typeof value.uid !== 'string' || !value.uid) return null;
  const rarity: Rarity = value.rarity === 'odd' || value.rarity === 'wrong' || value.rarity === 'relic' ? value.rarity : 'plain';
  const tier = typeof value.tier === 'number' && Number.isFinite(value.tier) ? Math.max(1, Math.min(MAX_TIER, Math.round(value.tier))) : BASES[value.base].tier;
  const affixes: Affix[] = [];
  if (Array.isArray(value.affixes)) {
    for (const a of value.affixes) {
      if (!a || typeof a !== 'object') continue;
      const { stat, v } = a as { stat?: unknown; v?: unknown };
      if (typeof stat === 'string' && stat in STAT_LABEL && typeof v === 'number' && Number.isFinite(v)) affixes.push({ stat: stat as Stat, v: Math.round(v) });
    }
  }
  const item: ItemInst = { uid: value.uid.slice(0, 24), base: value.base, rarity, tier, affixes };
  if (rarity === 'wrong' && typeof value.name === 'string') item.name = value.name.slice(0, 40);
  return item;
}

/** Old saves kept base ids only. Quest pieces come back as relics, the rest as plain. */
export function upgradeLegacy(id: string): ItemInst | null {
  if (!isBaseId(id)) return null;
  return makeItem(id, BASES[id].tier, 'plain');
}

// ---------------------------------------------------------------------------
// Stats.

export type Gear = Record<EquipSlot, ItemInst | null>;

export function emptyGear(): Gear {
  return { hand: null, sling: null, head: null, chest: null, legs: null, hands: null, charm: null };
}

export function slotFits(base: Base, slot: EquipSlot): boolean {
  if (slot === 'hand' || slot === 'sling') return base.slot === 'weapon';
  return base.slot === slot;
}

export const BASE_HP = 40;
export const BASE_SPEED = 78;
export const JACKLEG_SPEED = 100;
export const FIST = { dmg: 5, range: 26, cd: 0.4 };

export type Attack = {
  item: ItemInst | null;
  base: Base | null;
  dmg: number;
  cd: number;
  range: number;
  crit: number;
  stun: number;
  leech: number;
};

export type Stats = {
  maxHp: number;
  armor: number;
  speed: number;
  glow: number;
  mend: number;
  thorns: number;
  tabs: number;
  find: number;
  melee: Attack;
  ranged: Attack | null;
};

function sumAffixes(items: (ItemInst | null)[]): Record<Stat, number> {
  const total: Record<Stat, number> = { dmg: 0, pct: 0, armor: 0, hp: 0, glow: 0, stun: 0, fast: 0, speed: 0, leech: 0, mend: 0, thorns: 0, tabs: 0, crit: 0, reach: 0, find: 0 };
  for (const item of items) {
    if (!item) continue;
    for (const a of item.affixes) total[a.stat] += a.v;
  }
  return total;
}

function attackOf(item: ItemInst | null, body: Record<Stat, number>, bonus: number): Attack {
  const base = item ? BASES[item.base] ?? null : null;
  const own = sumAffixes([item]);
  const raw = (base?.dmg ?? FIST.dmg) + bonus + body.dmg + own.dmg;
  const pct = body.pct + own.pct;
  const fast = Math.min(60, body.fast + own.fast);
  return {
    item,
    base,
    dmg: Math.max(1, Math.round(raw * (1 + pct / 100))),
    cd: Math.round(((base?.cd ?? FIST.cd) / (1 + fast / 100)) * 1000) / 1000,
    range: (base?.range ?? FIST.range) + own.reach,
    crit: Math.min(50, body.crit + own.crit),
    stun: Math.min(40, body.stun + own.stun),
    leech: body.leech + own.leech,
  };
}

/** Sets. Wear more of one and it adds its bonus on top, as if one more piece had it. */
export const SETS: Record<string, { name: string; bonus: { n: number; affixes: Affix[] }[] }> = {
  through: {
    name: 'The Through Fare',
    bonus: [
      { n: 2, affixes: [{ stat: 'speed', v: 10 }, { stat: 'find', v: 20 }] },
      { n: 3, affixes: [{ stat: 'pct', v: 20 }, { stat: 'leech', v: 2 }] },
    ],
  },
  stopped: {
    name: 'The Stopped Clock',
    bonus: [
      { n: 2, affixes: [{ stat: 'armor', v: 3 }, { stat: 'hp', v: 15 }] },
      { n: 3, affixes: [{ stat: 'thorns', v: 6 }, { stat: 'mend', v: 1 }, { stat: 'pct', v: 10 }] },
    ],
  },
};

export function setPieces(set: string): string[] {
  return BASE_LIST.filter((base) => base.set === set).map((base) => base.id);
}

/** How many pieces of each set are worn. */
export function setCounts(gear: Gear): Record<string, number> {
  const out: Record<string, number> = {};
  const seen = new Set<string>();
  for (const slot of EQUIP_SLOTS) {
    const item = gear[slot];
    const set = item ? BASES[item.base]?.set : undefined;
    if (!item || !set || seen.has(item.base)) continue;
    seen.add(item.base);
    out[set] = (out[set] ?? 0) + 1;
  }
  return out;
}

/** The set bonus affixes that are on right now, as one fake item. */
function setBonus(gear: Gear): ItemInst | null {
  const affixes: Affix[] = [];
  for (const [set, n] of Object.entries(setCounts(gear))) for (const step of SETS[set]?.bonus ?? []) if (n >= step.n) affixes.push(...step.affixes);
  return affixes.length ? { uid: 'set', base: 'set', rarity: 'relic', tier: 1, affixes } : null;
}

/** THE place a person's numbers come from. */
export function computeStats(origin: OriginId, gear: Gear): Stats {
  const body = sumAffixes([gear.head, gear.chest, gear.legs, gear.hands, gear.charm, setBonus(gear)]);
  let armor = origin === 'carapace' ? 2 : 0;
  for (const slot of ['head', 'chest', 'legs', 'hands'] as const) {
    const item = gear[slot];
    if (item) armor += BASES[item.base]?.armor ?? 0;
  }
  const weapons = [gear.hand, gear.sling];
  const both = sumAffixes(weapons);
  const meleeItem = weapons.find((item) => item && BASES[item.base]?.kind === 'melee') ?? null;
  const rangedItem = weapons.find((item) => item && BASES[item.base]?.kind === 'ranged') ?? null;
  return {
    maxHp: BASE_HP + body.hp + both.hp,
    armor: armor + body.armor,
    speed: (origin === 'jackleg' ? JACKLEG_SPEED : BASE_SPEED) * (1 + Math.min(40, body.speed + both.speed) / 100),
    glow: body.glow,
    mend: body.mend + both.mend,
    thorns: body.thorns,
    tabs: body.tabs,
    find: body.find,
    melee: attackOf(meleeItem, body, origin === 'knuckle' ? 4 : 0),
    ranged: rangedItem ? attackOf(rangedItem, body, origin === 'lamp' ? 3 : 0) : null,
  };
}

/** Soak is flat, but a hit always gets a third of itself through. */
export function soaked(raw: number, armor: number): number {
  return Math.max(1, Math.ceil(raw / 3), Math.round(raw - armor));
}

/** Tooltip lines for a piece, shared by every panel. */
export function describe(item: ItemInst): string[] {
  const base = BASES[item.base];
  if (!base) return [];
  const lines: string[] = [];
  if (base.kind === 'melee') lines.push(`${base.dmg} damage · ${(1 / (base.cd ?? 1)).toFixed(1)} swings/s · reach ${base.range}`);
  if (base.kind === 'ranged') {
    const kind =
      base.shot === 'laser' ? 'beam' : base.shot === 'fan' ? `${base.pellets} bolts` : base.shot === 'stun' ? 'stuns' : base.shot === 'pop' ? 'blooms' : base.shot === 'bell' ? 'bloom that stuns' : base.shot === 'suck' ? 'bloom that pulls' : 'bolt';
    lines.push(`${base.dmg} damage · ${(1 / (base.cd ?? 1)).toFixed(1)} shots/s · ${kind}`);
  }
  if (base.armor) lines.push(`Soaks ${base.armor}`);
  for (const a of item.affixes) lines.push(STAT_LABEL[a.stat](a.v));
  const set = base.set ? SETS[base.set] : undefined;
  if (set && base.set) {
    lines.push(`${set.name} (${setPieces(base.set).map((id) => BASES[id]?.name).join(', ')})`);
    for (const step of set.bonus) lines.push(`  Wear ${step.n}: ${step.affixes.map((a) => STAT_LABEL[a.stat](a.v)).join(', ')}`);
  }
  return lines;
}

export function slotLabel(base: Base): string {
  if (base.slot === 'weapon') return base.kind === 'melee' ? 'Swung' : 'Shot';
  return base.slot === 'charm' ? 'Charm' : base.slot === 'hands' ? 'Hands' : base.slot[0].toUpperCase() + base.slot.slice(1);
}
